// All languages and the translation helpers, shared by the app and the server's evening tip job.
// Texts are either plain strings or functions that build a string from values (counts, names, formatted numbers).
import { en, type Strings } from "./lang/en.ts";
import { fi } from "./lang/fi.ts";

export type { Strings };
export const STRINGS = { en, fi } satisfies Record<string, Strings>;
export type LangCode = keyof typeof STRINGS;
export const isLang = (code: string): code is LangCode => code in STRINGS;

/** Keys whose value is a text (a string, or a function returning one). */
export type TextKey = { [K in keyof Strings]: Strings[K] extends string | ((...args: never[]) => string) ? K : never }[keyof Strings];
/** Keys whose value is a list of eight compass directions or moon phases. */
export type ListKey = "compass" | "windNames" | "dirFrom" | "dirTo" | "moonPhases";
/** Keys whose value maps codes to names (species, methods, weather codes...). */
export type MapKey = "wmo" | "methods" | "species" | "pinTypes" | "huntStyles" | "trend";
type TextArgs<K extends TextKey> = Strings[K] extends (...args: infer A) => string ? A : [];

export type Translate = <K extends TextKey>(key: K, ...args: TextArgs<K>) => string;
export type ListOf = (key: ListKey) => readonly string[];
export type NameOf = (group: MapKey, code: string | number) => string | undefined;

/** Translation functions for whichever language `lang()` returns at the moment of the call. */
export function translator(lang: () => string): { t: Translate; list: ListOf; nameOf: NameOf } {
  const current = (): Strings => { const code = lang(); return isLang(code) ? STRINGS[code] : en; };
  const t: Translate = (key, ...args) => {
    const v = current()[key] ?? en[key] as unknown;
    if (v === undefined) { console.warn("Missing text:", key); return key; } // only possible with a key built at runtime
    return typeof v === "function" ? (v as (...a: unknown[]) => string)(...args) : String(v);
  };
  const list: ListOf = (key) => current()[key];
  const nameOf: NameOf = (group, code) => (current()[group] as Record<string, string>)[code] ?? (en[group] as Record<string, string>)[code];
  return { t, list, nameOf };
}

/** Index 0–7 into an eight-point compass list (N, NE, E...) for a direction in degrees. */
export const compassIndex = (deg: number): number => Math.round(((deg % 360) + 360) % 360 / 45) % 8;
